import { inject, signal } from '@angular/core'
import { OptionsConfig } from '../../configs'
import { CoreService } from '../../services'
import { StorageKeyService } from '../../services/storage-key.service'

type StorageKey = { type: string; key: string }

const sortProp = signal('id')
const sortDir = signal('desc')

export abstract class BaseListing {
  refreshInterval: any = null
  private refreshAttempt = 0

  storageKey = signal<StorageKey>({ type: '', key: '' })
  protected keyService = inject(StorageKeyService)

  protected constructor(protected core: CoreService) {}

  getStorageKey() {
    // todo
    const key = this.keyService.getStorageKey()
    if (key) {
      this.storageKey.set(key)
    } else {
      this.core.services.oauth.logout()
    }
  }

  callAction(event: any): void {
    this[event.action](event.item)
  }

  getStorageKeyForEntity(entity: string): StorageKey {
    return this.keyService.getStorageKeyForEntity(entity, 'baseListing')
  }

  // Per-user key so a listing's persisted filters/sort belong to one account and never bleed
  // into another after an in-app account switch. Falls back to the legacy shared key only when
  // no account is known yet (e.g. before the profile has loaded).
  private viewSettingsKey(viewName: string): StorageKey {
    const base = this.core.services.storage.keys.views[viewName]
    const email: string =
      this.core.services.storage.getSync(this.core.services.storage.keys.auth.email) ||
      this.core.services.oauth.session?.email ||
      ''
    return email ? { key: `${base.key}.${email}`, type: base.type } : base
  }

  saveViewSettings(viewName: string, data: any): void {
    this.core.services.storage.set(this.viewSettingsKey(viewName), data)
  }

  loadViewSettings(viewName: string): any {
    const key = this.viewSettingsKey(viewName)
    const value = this.core.services.storage.getSync(key)
    if (value !== undefined) return value

    // Nothing under the per-email key yet: fall back to the pre-account-scoping legacy key
    // (installs that saved filters before this feature shipped) and migrate it forward so it
    // isn't silently lost, and isn't read back by a different account next time either.
    const legacyKey = this.core.services.storage.keys.views[viewName]
    if (key.key === legacyKey.key) return undefined
    const legacyValue = this.core.services.storage.getSync(legacyKey)
    if (legacyValue !== undefined) {
      this.core.services.storage.set(key, legacyValue)
      this.core.services.storage.clear(legacyKey)
    }
    return legacyValue
  }

  loadSorting(listName: string): void {
    const sorting = this.core.services.storage.getSync(this.getStorageKeyForEntity(listName))
    if (sorting?.sortProp) sortProp.set(sorting?.sortProp)
    else sortProp.set(sortProp())
    if (sorting?.sortDir) sortDir.set(sorting?.sortDir)
    else sortDir.set(sortDir())
  }

  downloadReport(report: any): void {
    this.core.services.download.downloadDocumentAndShowInNewWindow(report.url)
  }

  startRefreshing(reloadFn = 'filter', page = 1): void {
    if (this.refreshInterval) return

    // Reset attempts at the start of a new refresh session
    this.refreshAttempt = 0

    const scheduleNext = () => {
      // Determine delay based on attempt number: 5s, 10s, 15s, then 20s afterwards
      const attempt = this.refreshAttempt
      const base = OptionsConfig.strings.refreshIntervalTimeMS // acts as the max cap (e.g., 20s)
      const delays = [5_000, 10_000, 15_000]
      const delay = attempt < delays.length ? delays[attempt] : base

      this.refreshInterval = setTimeout(() => {
        // Perform the refresh action
        if (page === 1) this[reloadFn].refetch()
        else if (page > 1) this[reloadFn].fetchNextPage()

        // Prepare next cycle only if still active
        if (this.refreshInterval) {
          this.refreshAttempt += 1
          scheduleNext()
        }
      }, delay)
    }

    scheduleNext()
  }

  stopRefreshing(): void {
    if (this.refreshInterval) {
      // Clear any pending timer
      clearTimeout(this.refreshInterval)
      // Also clear interval just in case previous versions used it
      clearInterval(this.refreshInterval)
      this.refreshInterval = undefined
    }
    this.refreshAttempt = 0
  }
}
